import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import {
  ClearButton,
  DatePicker,
  SelectDropDown
} from 'components/common/Elements/Elements';

import { createFilterCountry, createFilterSize } from 'utils/common';
import AutocompleteFilter from './AutocompleteFilter';

import classes from './FiltersBlock.module.scss';

const FiltersBlock = ({
  filter,
  setFilter,
  startFilter,
  setStartFilter,
  filters,
  selectLoader,
  resetAllfilters,
  filterButton,
  setVisibleFilterSave,
  setVisibleFilterEdit,
  changeFilter,
  trigerResetDatePicker
}) => {
  const { t } = useTranslation(['activity']);
  const [autocompleteOptionsMultiple, setAutocompleteOptionsMultiple] =
    useState([]);

  const numberOfDatePickersProd = 3;
  const numberOfFilters =
    Object.keys(filters).length + numberOfDatePickersProd + 1;

  let companyCountry = [];
  let companySize = [];

  const handleFilter = e => {
    setFilter({ ...filter, [e.target]: e.value, currentPage: 1 });
    setVisibleFilterSave('button');
    setStartFilter(!startFilter);
    if (changeFilter !== 'Default') {
      setVisibleFilterEdit(true);
    }
  };

  function checkAndSetFilter(key, value) {
    if (!isEqual(filter[key], value)) {
      setFilter({ ...filter, [key]: value, currentPage: 1 });
      // in cross filters after changing the date you need to get new filters
      setStartFilter(!startFilter);
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
  }

  (function () {
    createFilterCountry(filters.countryCode, companyCountry);
    createFilterSize(filters.companySize, companySize);
  })();

  const handleDateChange = value => {
    const key = 'firstPaymentDate';
    checkAndSetFilter(key, value);
  };
  const handleBillDate = value => {
    const key = 'nextBillingDate';
    checkAndSetFilter(key, value);
  };
  const handleInvDate = value => {
    const key = 'firstInvoiceAt';
    checkAndSetFilter(key, value);
  };

  return (
    <div className={cn(filterButton ? classes.filters : classes.filters_close)}>
      <>
        <DatePicker
          label={t('activity:filters.FIRST_PAYMENT')}
          input={{
            value: filter.firstPaymentDate,
            onChange: handleDateChange
          }}
          trigerResetDatePicker={trigerResetDatePicker}
        />
        <DatePicker
          label={t('activity:filters.NEXT_BILLING')}
          input={{
            value: filter.nextBillingDate,
            onChange: handleBillDate
          }}
          trigerResetDatePicker={trigerResetDatePicker}
        />
        <DatePicker
          label={t('activity:filters.FIRST_INVOICE')}
          input={{
            value: filter.firstInvoiceAt,
            onChange: handleInvDate
          }}
          trigerResetDatePicker={trigerResetDatePicker}
        />
      </>
      {filters.plan && (
        <SelectDropDown
          options={filters.plan}
          input={{ value: filter.plan, onChange: handleFilter }}
          name={'plan'}
          label={t('activity:filters.PRICING_PLAN')}
          multi
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {filters.healthStatus && (
        <SelectDropDown
          options={filters.healthStatus}
          input={{ value: filter.healthStatus, onChange: handleFilter }}
          name={'healthStatus'}
          label={t('activity:filters.HEALTH_STATUS')}
          multi
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {filters.billingCycle && (
        <SelectDropDown
          options={filters.billingCycle}
          input={{
            value: filter.billingCycle,
            onChange: handleFilter
          }}
          name={'billingCycle'}
          label={t('activity:filters.BILLING_CYCLE')}
          multi
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {filters.status && (
        <SelectDropDown
          options={filters.status}
          input={{ value: filter.status, onChange: handleFilter }}
          name={'status'}
          label={t('activity:filters.CUSTOMER_STATUS')}
          multi
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {companySize && (
        <SelectDropDown
          options={companySize}
          input={{ value: filter.companySize, onChange: handleFilter }}
          name={'companySize'}
          label={t('activity:filters.COMPANY_SIZE')}
          multi
          renderKey={'label'}
          reload
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          selectLoader={selectLoader}
        />
      )}
      {companyCountry.length > 0 && (
        <SelectDropDown
          options={companyCountry}
          input={{ value: filter.country, onChange: handleFilter }}
          name={'country'}
          label={t('activity:filters.COUNTRY')}
          searchKeys={['value', 'findName']}
          renderKey={'fullName'}
          checkRenderKey={'fullName'}
          multi
          searchable
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {filters.industry && (
        <SelectDropDown
          options={filters.industry}
          input={{ value: filter.industry, onChange: handleFilter }}
          name={'industry'}
          label={t('activity:filters.INDUSTRY')}
          multi
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {filters.partnerType && (
        <SelectDropDown
          options={filters.partnerType}
          input={{ value: filter.partnerType, onChange: handleFilter }}
          name={'partnerType'}
          label={t('activity:filters.PARTNER_TYPE')}
          multi
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {filters.region && (
        <SelectDropDown
          options={filters.region}
          input={{ value: filter.region, onChange: handleFilter }}
          name={'region'}
          label={t('activity:filters.REGION')}
          multi
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {filters.customerSuccessEngineer && (
        <SelectDropDown
          options={filters.customerSuccessEngineer}
          input={{
            value: filter.customerSuccessEngineer,
            onChange: handleFilter
          }}
          name={'customerSuccessEngineer'}
          label={t('activity:about.CS_ENGINEER')}
          multi
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {filters.accountManager && (
        <SelectDropDown
          options={filters.accountManager}
          input={{ value: filter.accountManager, onChange: handleFilter }}
          name={'accountManager'}
          label={t('activity:filters.ACCOUNT_MANAGER_')}
          multi
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      {filters.isTestTenant && (
        <SelectDropDown
          options={
            filters['isTestTenant']
              ? filters['isTestTenant'].map(item =>
                  item === true ? 'True' : 'False'
                )
              : []
          }
          input={{ value: filter.isTestTenant, onChange: handleFilter }}
          name={'isTestTenant'}
          label={t('activity:filters.IS_TEST_TENANT')}
          closeTrigger={{
            trigger: startFilter,
            setTrigger: setStartFilter
          }}
          reload
          selectLoader={selectLoader}
        />
      )}
      <AutocompleteFilter
        autocompleteOptionsMultiple={autocompleteOptionsMultiple}
        setAutocompleteOptionsMultiple={setAutocompleteOptionsMultiple}
        handleFilter={handleFilter}
      />
      <ClearButton
        className={classes.clear}
        onClick={() => {
          setAutocompleteOptionsMultiple([]);
          resetAllfilters(false, true, false, true);
        }}
        numberОfFilters={numberOfFilters}
      />
    </div>
  );
};

FiltersBlock.propTypes = {
  filter: PropTypes.object,
  setFilter: PropTypes.func,
  startFilter: PropTypes.bool,
  setStartFilter: PropTypes.func,
  filterButton: PropTypes.bool,
  filters: PropTypes.object,
  selectLoader: PropTypes.bool,
  trigerResetDatePicker: PropTypes.bool,
  resetAllfilters: PropTypes.func,
  setVisibleFilterSave: PropTypes.func,
  setVisibleFilterEdit: PropTypes.func,
  changeFilter: PropTypes.string
};

export default FiltersBlock;
